// Renders a validated document to React elements.
//
// SECURITY-CRITICAL (FR-015, SC-006). Every branch below emits a React element
// or a string. `dangerouslySetInnerHTML` appears nowhere in this file, and must
// not be introduced: React escapes string children, so a submitted value
// containing `<script>` renders as visible characters.
//
// The function is total over the schema — there is no "unknown node" branch that
// could fall back to raw HTML, because the schema has no unknown nodes.

import type { JSX } from 'react';
import type { BlockNode, InlineNode, Paragraph, TemplateDocument } from '@template-filler/core';

function renderInline(node: InlineNode, key: number): JSX.Element | string {
  if (node.type === 'hardBreak') return <br key={key} />;

  if (node.type === 'placeholder') {
    return (
      <span key={key} className="placeholder-chip" data-placeholder-name={node.attrs.name}>
        {node.attrs.name}
      </span>
    );
  }

  // node.type === 'text' — the value lands here as a string child, which React
  // escapes. This is the line that makes SC-006 hold.
  let element: JSX.Element | string = node.text;
  for (const mark of node.marks ?? []) {
    if (mark.type === 'bold') element = <strong key={key}>{element}</strong>;
    if (mark.type === 'italic') element = <em key={key}>{element}</em>;
    if (mark.type === 'underline') element = <u key={key}>{element}</u>;
  }
  return typeof element === 'string' ? element : <span key={key}>{element}</span>;
}

function renderParagraph(node: Paragraph, key: number): JSX.Element {
  return <p key={key}>{(node.content ?? []).map(renderInline)}</p>;
}

function renderBlock(node: BlockNode, key: number): JSX.Element {
  switch (node.type) {
    case 'paragraph':
      return renderParagraph(node, key);

    case 'heading': {
      const children = (node.content ?? []).map(renderInline);
      if (node.attrs.level === 1) return <h1 key={key}>{children}</h1>;
      if (node.attrs.level === 2) return <h2 key={key}>{children}</h2>;
      return <h3 key={key}>{children}</h3>;
    }

    case 'bulletList':
      return (
        <ul key={key}>
          {node.content.map((item, i) => (
            <li key={i}>{item.content.map(renderParagraph)}</li>
          ))}
        </ul>
      );

    case 'orderedList':
      return (
        <ol key={key}>
          {node.content.map((item, i) => (
            <li key={i}>{item.content.map(renderParagraph)}</li>
          ))}
        </ol>
      );
  }
}

export function DocumentView({ doc }: { doc: TemplateDocument }): JSX.Element {
  return <div className="document">{doc.content.map(renderBlock)}</div>;
}
